import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from './config';
import { useAuth } from './AuthContext';

// Resolution-shaped notifications (TODO.md design seed 2026-07-05). The bell
// counts DERIVED events — nothing is stored server-side but a per-user
// watermark; opening the inbox lists them and "Caught up" advances the
// watermark. Kinds are resolution-shaped only: activation (your edge went
// live), challenge (a claim you kept got a counter), crossing (contested ↔
// survives division), restates (an unvoted restates suggestion on your
// claim). Karma and "someone replied" never appear here by design.

const KIND_LABEL = {
  activation: 'went live',
  challenge: 'challenged',
  crossing: 'state change',
  restates: 'restates?',
};

const KIND_COLOR = {
  activation: 'var(--wv-pos)',
  challenge: 'var(--wv-neg)',
  crossing: 'var(--wv-meta)',
  restates: 'var(--wv-warn)',
};

function NotificationsBell() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/api/notifications');
      if (res.ok) {
        const data = await res.json();
        setEvents(data.events || []);
      }
    } catch {
      // the bell is never worth an error state — stay quiet
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) load();
    else setEvents([]);
  }, [user, load]);

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [open]);

  const markSeen = async () => {
    try {
      const res = await apiFetch('/api/notifications/seen', { method: 'POST' });
      if (res.ok) setEvents([]);
    } catch {
      // leave the list; the next load will tell the truth
    }
  };

  if (!user) return null;

  return (
    <span ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => {
          if (!open) load();
          setOpen(!open);
        }}
        title="Resolution notifications — activations, challenges, state crossings. Never karma."
        aria-label={`Notifications${events.length ? ` (${events.length})` : ''}`}
        style={{
          border: 'none',
          background: 'none',
          cursor: 'pointer',
          padding: 0,
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          color: events.length > 0 ? 'var(--nav-ink, #000)' : 'var(--nav-ink-dim, #666)',
        }}
      >
        {/* token-inked bell (was the 🔔 emoji — off-theme; 2026-07-25 pass) */}
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.7 21a2 2 0 0 1-3.4 0" />
        </svg>
        {events.length > 0 && (
          <span
            style={{
              position: 'absolute',
              top: '-6px',
              right: '-10px',
              background: 'var(--wv-neg)',
              color: 'var(--wv-bg)',
              borderRadius: '999px',
              fontSize: '10px',
              lineHeight: 1,
              padding: '3px 5px',
              fontWeight: 'bold',
            }}
          >
            {events.length}
          </span>
        )}
      </button>
      {open && (
        <div className="bell-drop">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '10px 14px',
              borderBottom: '1px solid var(--wv-border)',
            }}
          >
            <span style={{ fontSize: '13px', fontWeight: 'bold', color: 'var(--wv-text)' }}>
              Since you last looked
            </span>
            {events.length > 0 && (
              <button onClick={markSeen} style={{ fontSize: '12px', cursor: 'pointer' }}>
                Caught up
              </button>
            )}
          </div>
          {loading && events.length === 0 ? (
            <div style={{ padding: '14px', fontSize: '13px', color: 'var(--wv-muted)' }}>Deriving…</div>
          ) : events.length === 0 ? (
            <div style={{ padding: '14px', fontSize: '13px', color: 'var(--wv-muted)' }}>
              Nothing has resolved, activated, or been challenged. Quiet is honest.
            </div>
          ) : (
            events.map((e, i) => (
              <Link
                key={`${e.kind}-${e.edge_id || e.claim_id || i}`}
                to={e.link}
                onClick={() => setOpen(false)}
                className="wv-plainlink"
                style={{
                  display: 'block',
                  padding: '10px 14px',
                  borderBottom: '1px solid var(--wv-border)',
                  color: 'var(--wv-text)',
                }}
              >
                <span
                  style={{
                    fontSize: '10px',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: KIND_COLOR[e.kind] || 'var(--wv-muted)',
                  }}
                >
                  {KIND_LABEL[e.kind] || e.kind}
                </span>
                <div style={{ fontSize: '13px', marginTop: '3px', lineHeight: 1.45 }}>
                  {e.sentence}
                </div>
              </Link>
            ))
          )}
        </div>
      )}
    </span>
  );
}

export default NotificationsBell;
